/**
 * DEMA-WMA Gainer LIVE trading panel: shows every gate that decides whether
 * REAL broker orders are sent, and the typed live approval / revoke. Approval
 * saves and activates a new config version that changes only `live_trading`.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, FormControlLabel, Stack, TextField, Typography,
} from "@mui/material";
import { type DemaWmaLiveStatus } from "@/services/demaWmaGainerService";
import { useDemaWmaService } from "@/services/demaWmaServiceContext";
import { extractErrorMessage } from "@/utils/errors";

const GATE_LABELS: Record<string, string> = {
  env_live_execution_enabled: "Server live flag",
  platform_live_switch_on: "Platform live switch",
  broker_account_selected: "Broker account selected",
  broker_session_present: "Broker session",
  broker_supported_for_orders: "Broker order support",
  strategy_live_approved: "Strategy live approval",
  strategy_running: "Strategy running",
};

export default function DemaWmaLivePanel({ onChanged }: { onChanged?: () => void }) {
  const demaWmaGainerService = useDemaWmaService();
  const [st, setSt] = useState<DemaWmaLiveStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [instruments, setInstruments] = useState<string[]>(["NIFTY"]);
  const [maxLots, setMaxLots] = useState("1");
  const [maxLoss, setMaxLoss] = useState("");
  const [phrase, setPhrase] = useState("");

  const load = useCallback(async () => {
    try {
      setSt(await demaWmaGainerService.getLiveStatus());
      setError(null);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  }, [demaWmaGainerService]);
  useEffect(() => { void load(); }, [load]);

  const submit = async (enable: boolean) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await demaWmaGainerService.setLiveApproval(enable
        ? { enable, confirmation: phrase, instruments, max_lots: Number(maxLots), max_daily_loss: Number(maxLoss) }
        : { enable });
      setMsg(`${enable ? "Live trading approved" : "Live trading revoked"}: version ${r.version} is now active.`);
      setPhrase("");
      await load();
      onChanged?.();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!st) return error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null;
  const approved = st.gates.strategy_live_approved;
  const lt = st.live_trading || { enabled: false };
  const toggle = (idx: string) => setInstruments((cur) => (cur.includes(idx) ? cur.filter((i) => i !== idx) : [...cur, idx]));

  return (
    <Card variant="outlined" sx={{ mb: 2 }} data-testid="live-panel">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>Admin live instance (this page)</Typography>
          <Chip color={st.live_orders_will_be_sent ? "error" : "default"}
            label={st.live_orders_will_be_sent ? "REAL ORDERS ON" : "No real orders"} />
        </Stack>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          {Object.entries(st.gates).map(([k, v]) => (
            <Chip key={k} size="small" variant={v ? "filled" : "outlined"} color={v ? "success" : "default"}
              label={`${v ? "✓" : "✗"} ${GATE_LABELS[k] ?? k}`} />
          ))}
          {st.entries_paused && <Chip size="small" color="warning" label="Entries paused" />}
        </Box>
        {st.open_live_positions > 0 && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {st.open_live_positions} LIVE position(s) open. Keep the platform live switch ON until they are flat, or their exits cannot be sent.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
        {msg && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setMsg(null)}>{msg}</Alert>}
        {approved ? (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              Approved on v{lt.approved_version} by {lt.approved_by}: {(lt.instruments || []).join(", ")}, max {lt.max_lots} lot(s),
              daily loss cap ₹{lt.max_daily_loss}. Stop instantly with the kill switch below.
            </Typography>
            <Button color="warning" variant="outlined" disabled={busy} onClick={() => void submit(false)}>Revoke live approval</Button>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Approving saves a new version (only live_trading changes) and activates it. Refused while any position is open.
            </Typography>
            <Stack direction="row" spacing={1}>
              {["NIFTY", "BANKNIFTY"].map((idx) => (
                <FormControlLabel key={idx} label={idx}
                  control={<Checkbox size="small" checked={instruments.includes(idx)} onChange={() => toggle(idx)} />} />
              ))}
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField size="small" label="Max lots per trade (1–5)" type="number" value={maxLots} onChange={(e) => setMaxLots(e.target.value)} />
              <TextField size="small" label="Daily loss cap (₹)" type="number" value={maxLoss} onChange={(e) => setMaxLoss(e.target.value)} />
            </Stack>
            <TextField size="small" label={`Type: ${st.confirmation_phrase}`} value={phrase} onChange={(e) => setPhrase(e.target.value)} />
            <Box>
              <Button color="error" variant="contained"
                disabled={busy || phrase.trim() !== st.confirmation_phrase || instruments.length === 0 || !(Number(maxLoss) > 0)}
                onClick={() => void submit(true)}>
                Approve live trading
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
